// Display helpers shared between the Invoices list and detail views.
// Single source of truth so the two views can't drift apart.

import { InvoiceStatus } from '@/enums/InvoiceStatus'
import { INVOICE_TYPES } from '@/enums/InvoiceType'

const CUSTOMER_TYPE_BUSINESS = 'customer-business'
const CUSTOMER_TYPE_PRIVATE = 'customer-private'

type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast'

export const formatAmount = (raw: string | number | null | undefined): string =>
  raw !== null && raw !== undefined && raw !== '' ? `${Number(raw).toFixed(2)} €` : '—'

// Payments/Transactions store the amount as an integer number of cents.
export const formatCents = (cents: number | null | undefined, currency?: string | null): string =>
  cents !== null && cents !== undefined ? `${(cents / 100).toFixed(2)} ${currency || '€'}` : '—'

// An Order line item struck through in the charged-items table when its order
// was cancelled or deleted (mirrors the old backoffice).
export const isVoidedItem = (status: string | null | undefined): boolean =>
  !!status && /cancel|delet/i.test(status)

const STATUS_SEVERITY: Record<string, TagSeverity> = {
  [InvoiceStatus.DRAFT]: 'secondary',
  [InvoiceStatus.OPEN]: 'info',
  [InvoiceStatus.SETTLED]: 'success',
  [InvoiceStatus.VOID]: 'danger',
  [InvoiceStatus.UNCOLLECTIBLE]: 'danger',
  [InvoiceStatus.DELETED]: 'contrast'
}

export const invoiceStatusSeverity = (status: string | null | undefined): TagSeverity =>
  status ? STATUS_SEVERITY[status] ?? 'secondary' : 'secondary'

export const invoiceTypeLabel = (atType: string | null | undefined): string =>
  atType ? INVOICE_TYPES[atType]?.label ?? atType : '—'

export const isBusinessCustomer = (invoice: InvoiceListItem | InvoiceEntity): boolean =>
  invoice.customer?.userType === CUSTOMER_TYPE_BUSINESS || !!invoice.customer?.company

export const customerTypeLabel = (invoice: InvoiceListItem | InvoiceEntity): string => {
  if (invoice.customer?.userType === CUSTOMER_TYPE_BUSINESS || invoice.customer?.company) {
    return 'Business'
  }
  if (invoice.customer?.userType === CUSTOMER_TYPE_PRIVATE) {
    return 'Private'
  }
  return '—'
}

export const customerName = (invoice: InvoiceListItem | InvoiceEntity): string => {
  const c = invoice.customer
  if (!c) return '—'
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim()
  return name || c.email || '—'
}

// A collective PARENT (Sammelrechnung) — aggregates child d-invoices.
export const isCollectiveParent = (invoice: InvoiceListItem | InvoiceEntity): boolean =>
  invoice.collectiveInvoice === true

// A bundled CHILD d-invoice inside a collective parent.
export const isCollectiveChild = (invoice: InvoiceListItem | InvoiceEntity): boolean =>
  invoice.partOfCollectiveInvoice === true

// Cancel is only offered while the workflow allows it (cancel: [open, settled] -> void).
export const isCancellable = (invoice: InvoiceEntity | InvoiceListItem): boolean =>
  !invoice.cancelled &&
  (invoice.status === InvoiceStatus.OPEN || invoice.status === InvoiceStatus.SETTLED)

// #2033 — automated Stripe collection.
//
// There is deliberately no "succeeded" state on the invoice: a settled invoice IS the success signal,
// so the terminal case is derived here rather than stored twice and kept in step. Everything else
// mirrors App\Enum\AutoCollectionState.
const AUTO_COLLECTION_STATES: Record<string, { short: string; long: string; severity: string }> = {
  pending: { short: 'Scheduled', long: 'Pending', severity: 'info' },
  submitted: { short: 'Submitted', long: 'Submitted to the bank', severity: 'warn' },
  failed: { short: 'Retrying', long: 'Failed — retry scheduled', severity: 'warn' },
  escalated: { short: 'Escalated', long: 'Escalated — needs manual handling', severity: 'danger' },
  cancelled: {
    short: 'Cancelled',
    long: 'Cancelled — nothing will be collected',
    severity: 'secondary'
  },
  refunding: {
    short: 'Refund in progress',
    long: 'Refund in progress — Stripe has not confirmed it yet',
    severity: 'warn'
  },
  refunded: {
    short: 'Refunded',
    long: 'Refunded — the debit went through after the invoice was cancelled',
    severity: 'secondary'
  },
  covered: {
    short: 'Paid by earlier debit',
    long: 'Paid by the debit of the cancelled invoice it replaces',
    severity: 'info'
  },
  held: {
    short: 'Waiting for refund',
    long: 'Waiting for the refund of the debit of the cancelled invoice it replaces',
    severity: 'info'
  }
}

const COLLECTED = { short: 'Collected', long: 'Collected', severity: 'success' }

export const autoCollectionStateLabel = (state: string): string =>
  AUTO_COLLECTION_STATES[state]?.long ?? state

export const autoCollectionFilterOptions: { label: string; value: string }[] = [
  { label: AUTO_COLLECTION_STATES.pending.short, value: 'pending' },
  { label: AUTO_COLLECTION_STATES.submitted.short, value: 'submitted' },
  { label: COLLECTED.short, value: 'collected' },
  ...Object.entries(AUTO_COLLECTION_STATES)
    .filter(([value]) => !['pending', 'submitted'].includes(value))
    .map(([value, state]) => ({ label: state.short, value }))
]

// Finance can move an open invoice to the SEPA mandate of the company's current contact person, as long
// as no debit is running.
export const canSwitchCollectionMandate = (invoice: InvoiceEntity): boolean =>
  invoice.autoCollectionActive === true &&
  invoice.status === InvoiceStatus.OPEN &&
  ['pending', 'failed', 'escalated'].includes(invoice.autoCollectionState ?? '')

const SWITCH_MANDATE_ERRORS: Record<string, string> = {
  auto_collection_switch_not_possible:
    'This invoice cannot be switched now. A debit may be running or the invoice is no longer open.',
  auto_collection_switch_no_sepa_mandate:
    "The company's contact person has no SEPA mandate that can be debited.",
  auto_collection_switch_same_mandate: 'The invoice is already debited from this SEPA mandate.',
  auto_collection_notice_not_sent:
    'The email to the customer could not be sent, so nothing was changed. Please check the email template "auto-collection-notice".'
}

export const switchMandateErrorText = (key: string | undefined): string | undefined =>
  key ? SWITCH_MANDATE_ERRORS[key] : undefined

export const isAutoCollected = (invoice: InvoiceListItem | InvoiceEntity): boolean =>
  invoice.autoCollectionActive === true && invoice.status === InvoiceStatus.SETTLED

export const autoCollectionDisplay = (
  invoice: InvoiceListItem | InvoiceEntity,
  variant: 'short' | 'long' = 'short'
): { label: string; severity: string } => {
  if (isAutoCollected(invoice)) {
    return { label: COLLECTED[variant], severity: COLLECTED.severity }
  }

  const state = AUTO_COLLECTION_STATES[invoice.autoCollectionState ?? '']

  return {
    label: state?.[variant] ?? invoice.autoCollectionState ?? '—',
    severity: state?.severity ?? 'secondary'
  }
}
